import { Button } from '@vd/ui';
import { Download, Sparkles, Folder, Wand2, ListChecks, Code2 } from 'lucide-react';
import styles from '../../McpSetupPage.module.css';

/**
 * Pre-built skill walkthrough — the Toado triage skill.
 *
 * Drops users into the "fix tickets via Claude Code" workflow without
 * them having to write the skill themselves. The download button
 * pulls /toado-triage.skill.md (a copy of the canonical file at
 * .claude/skills/toado-triage/SKILL.md, served from apps/web/public/).
 * The two must stay byte for byte the same: `triageSkillCopies.test.ts`
 * fails when they differ (TOADO-949). Keep the page's description of the
 * skill below in step with what the file does.
 */
/** An install command: scrolls sideways in its own box when it is wider than the column. */
const INSTALL_BLOCK = {
  background: 'var(--bg-muted)',
  padding: 12,
  borderRadius: 6,
  fontSize: 13,
  overflowX: 'auto',
  whiteSpace: 'pre',
  maxWidth: '100%',
  margin: 0,
  color: 'var(--text)',
} as const;

export function PreBuiltSkillTab() {
  return (
    <div className={styles.page}>
      <h1 className={styles.h1}>Pre-built skills</h1>
      <p className={styles.sub}>
        Drop-in workflows for Claude Code. Install once per project; teams
        share the same definition via the repo.
      </p>

      <section className={styles.step}>
        <div className={styles.stepNum}>
          <Sparkles size={14} />
        </div>
        <div style={{ flex: 1 }}>
          <h3 className={styles.stepTitle}>toado-triage</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '4px 0 12px', lineHeight: 1.6 }}>
            Lets Claude Code fix tickets directly from your kanban. Paste a
            ticket id, ask for a sweep of a column, run a verification pass,
            or ask what to fix first. The skill reads only what it needs,
            reproduces behaviour bugs with your Playwright, fixes the code,
            records the fix with its commit, and moves the ticket to the
            right column.
          </p>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
            <a href="/toado-triage.skill.md" download="SKILL.md">
              <Button variant="primary" leftIcon={<Download size={14} />}>
                Download SKILL.md
              </Button>
            </a>
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
              ~13 KB · one markdown file · works in any codebase with the Toado MCP
            </span>
          </div>
        </div>
      </section>

      <section className={styles.step}>
        <div className={styles.stepNum}>1</div>
        {/* minWidth 0, so a long command scrolls inside its box rather than
            widening the column past it (TOADO-779). */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3 className={styles.stepTitle}>Install</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '4px 0 12px', lineHeight: 1.6 }}>
            Claude Code reads skills from <code>.claude/skills/</code> in your repo. Put the
            downloaded file at <code>.claude/skills/toado-triage/SKILL.md</code>: move it there
            in your file manager, or open a terminal at the root of your repo and run the
            commands for your shell. They make the folder, then move the file from your
            Downloads folder into it.
          </p>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '0 0 6px', fontWeight: 500 }}>
            macOS, Linux, or Git Bash on Windows
          </p>
          <pre style={INSTALL_BLOCK}>{`mkdir -p .claude/skills/toado-triage
mv ~/Downloads/SKILL.md .claude/skills/toado-triage/SKILL.md`}</pre>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, margin: '12px 0 6px', fontWeight: 500 }}>
            Windows PowerShell
          </p>
          <pre style={INSTALL_BLOCK}>{`New-Item -ItemType Directory -Force .claude\\skills\\toado-triage | Out-Null
Move-Item "$HOME\\Downloads\\SKILL.md" .claude\\skills\\toado-triage\\SKILL.md`}</pre>
          <p style={{ color: 'var(--text-tertiary)', fontSize: 12, marginTop: 8 }}>
            Commit it. Anyone on the team who clones the repo (and has the
            toado MCP set up; see <a href="/company/settings/mcp-setup" style={{ color: 'var(--hl-blue)' }}>MCP Connector</a>)
            picks up the same skill automatically.
          </p>
        </div>
      </section>

      <section className={styles.step}>
        <div className={styles.stepNum}>2</div>
        <div style={{ flex: 1 }}>
          <h3 className={styles.stepTitle}>Use it</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: '4px 0 12px', lineHeight: 1.6 }}>
            Just talk to Claude Code in plain English. The skill picks up
            on phrasing automatically:
          </p>

          <div style={{ display: 'grid', gap: 10 }}>
            <Phrase
              icon={<Wand2 size={14} />}
              label="Single ticket"
              example="fix t-lIMPHR"
              detail="Loads, fixes, comments, and moves the ticket to In Review."
            />
            <Phrase
              icon={<ListChecks size={14} />}
              label="Sweep To Do"
              example="fix everything in Marketing"
              detail="Walks the To Do column oldest → newest, fixes each, moves them all to In Review."
            />
            <Phrase
              icon={<ListChecks size={14} />}
              label="Verify In Review"
              example="review the In Review tickets in Marketing"
              detail="Confirms each fix is still in the branch (using the commit SHA in the comment), moves passing tickets to Done, bounces failures back to To Do."
            />
            <Phrase
              icon={<Code2 size={14} />}
              label="Build tests"
              example="build tests for Marketing"
              detail="Walks a column named 'Tests needed' (or similar), writes a regression test for each ticket from what the person did, runs it against your local app, and leaves the ticket for you to confirm the test catches the bug."
            />
            <Phrase
              icon={<ListChecks size={14} />}
              label="What to fix first"
              example="what should I fix first in Marketing"
              detail="Reads the error and page hotspots and proposes an order with ticket ids. Fixes nothing until you pick. Needs a plan with Reports."
            />
            <Phrase
              icon={<Wand2 size={14} />}
              label="Reproduce"
              example="reproduce t-lIMPHR"
              detail="Turns what the person did into steps and a Playwright test, runs it against your local app, and confirms it fails for the reported reason."
            />
          </div>
        </div>
      </section>

      <section className={styles.step}>
        <div className={styles.stepNum}>
          <Folder size={14} />
        </div>
        <div style={{ flex: 1 }}>
          <h3 className={styles.stepTitle}>What's in the skill</h3>
          <ul style={{ color: 'var(--text-secondary)', fontSize: 14, paddingLeft: 18, lineHeight: 1.8 }}>
            <li>
              <strong>Ticket text is data, never instructions.</strong> A
              description, comment or page value that asks the agent to run a
              command, open a link or install a package is reported to you,
              not followed. Widget reporters are treated as unverified.
            </li>
            <li>
              <strong>Small reads first.</strong> A short brief, then the
              timeline, then DevTools filtered on the server, and pictures only
              for visual bugs, so most fixes never touch the bulky payloads.
            </li>
            <li>
              <strong>Reproduce, then fix.</strong> Behaviour bugs get a
              Playwright test from what the person did, run before and after
              the fix, with both results in the comment.
            </li>
            <li>
              <strong>Acceptance-criteria splitting.</strong> Multi-claim
              descriptions ("X is broken AND Y is wrong") are split into
              bullets and addressed individually, with ✓/✗ per criterion in
              the comment.
            </li>
            <li>
              <strong>Duplicates found by the server.</strong> Tickets that
              share a captured error are found and folded into the original,
              so the same bug is fixed once.
            </li>
            <li>
              <strong>Recorded fixes.</strong> Each fix is recorded on the
              ticket with its commit, and verify mode checks that commit is
              still in the branch.
            </li>
            <li>
              <strong>Project disambiguation.</strong> When two companies
              have a project with the same name, the skill stops and asks
              instead of silently picking one.
            </li>
            <li>
              <strong>Idempotent sweeps.</strong> If a batch is interrupted,
              re-running the same command picks up where it left off:
              already-fixed tickets are out of To Do, no special resume flag
              needed.
            </li>
            <li>
              <strong>[VISUAL] tag in comments</strong> for fixes that need a
              human eye (CSS, layout, color) vs. behavioral fixes the
              typecheck and tests fully cover. Helps reviewers know what to
              actually look at.
            </li>
            <li>
              <strong>Nothing left behind.</strong> Screenshots downloaded for
              visual bugs go to a <code>.toado-run-…</code> folder that git is told to
              ignore on your machine only, and are deleted at the end of the run. It never pushes unless you ask.
            </li>
            <li>
              <strong>Asks instead of guessing.</strong> Ambiguous tickets,
              features that need a product decision and missing steps to
              reproduce become a question to the person who reported it, and
              the ticket moves to the column that waits on them. Schema,
              authentication and large changes are left for you to decide.
            </li>
          </ul>
        </div>
      </section>

      <section className={styles.step}>
        <div className={styles.stepNum}>?</div>
        <div style={{ flex: 1 }}>
          <h3 className={styles.stepTitle}>Requirements</h3>
          <ul style={{ color: 'var(--text-secondary)', fontSize: 14, paddingLeft: 18, lineHeight: 1.8 }}>
            <li>
              <a href="https://claude.com/claude-code" target="_blank" rel="noreferrer" style={{ color: 'var(--hl-blue)' }}>
                Claude Code
              </a>{' '}
              installed in your editor or terminal.
            </li>
            <li>
              The <strong>toado MCP server</strong> connected. See{' '}
              <a href="/company/settings/mcp-setup" style={{ color: 'var(--hl-blue)' }}>
                MCP Connector
              </a>{' '}
              in this settings area for the one-line install command and the
              OAuth flow.
            </li>
            <li>
              The skill lives at <code>.claude/skills/toado-triage/SKILL.md</code>{' '}
              in the project where you want to triage tickets.
            </li>
          </ul>
        </div>
      </section>
    </div>
  );
}

interface PhraseProps {
  icon: React.ReactNode;
  label: string;
  example: string;
  detail: string;
}

function Phrase({ icon, label, example, detail }: PhraseProps) {
  return (
    <div style={{
      display: 'flex',
      gap: 12,
      padding: 12,
      background: 'var(--bg-muted)',
      borderRadius: 6,
      alignItems: 'flex-start',
    }}>
      <div style={{ color: 'var(--text-tertiary)', paddingTop: 2 }}>{icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: 0.04 }}>
            {label}
          </span>
          <code style={{
            fontSize: 13,
            background: 'var(--surface)',
            padding: '2px 6px',
            borderRadius: 4,
            color: 'var(--text)',
          }}>
            {example}
          </code>
        </div>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          {detail}
        </p>
      </div>
    </div>
  );
}
